import React, { useLayoutEffect } from "react";
import { MonthView } from "../../views/MonthView";
import { ColumnView } from "../../views/ColumnView";
import { WeekView } from "../../views/WeekView";
import { AvailabilityView } from "../../views/Availability";
import { AvailabilityReview } from "../../views/AvailabilityReview";
import { Meeting } from "../../views/Meeting";
import { SCHEDULE_STATE } from "../../types";
import { useSchedulerStore } from "../../state/scheduler.store";
import { useSelectedSlot } from "../../hooks/useSelectedSlot";
import {
  useConfirmationPrevSlot,
  usePreviousSlot
} from "../../hooks/usePreviousSlot";
import { useMeetingStatus } from "../../hooks/useMeetingStatus";
import { useBookingForm } from "../../hooks/useBookingForm";
import { useCancellation } from "../../hooks/useCancellation";
import { initializeSchedulerStore } from "../../helpers/init";
import { useMeetingLinkTimezone } from "../../hooks/useMeetingLinkTimezone";
import { useScreenSize } from "../../hooks/useScreenSize";
import isTouchDevice from "is-touch-device";
import { useTranslation } from "react-i18next";

const SchedulerViews = ({
  currentView,
  currentlySelectedSlot,
  previouslySelectedTime,
  handleTimezoneChange,
  handleFormInput,
  actions
}) => {
  const { isSmallScreen, isMediumScreen, isLargeScreen } = useScreenSize();
  const isTouchScreen = isTouchDevice();

  useLayoutEffect(() => {
    const screenType = isSmallScreen
      ? "small"
      : isMediumScreen
        ? "medium"
        : "large";
    if (
      ((isSmallScreen || isMediumScreen) &&
        !supportedViews[screenType]?.includes(currentView)) ||
      (currentView === "week" && isTouchScreen)
    ) {
      actions.setUiView("month");
    }
  }, [isSmallScreen, isMediumScreen, isLargeScreen, currentView]);

  const supportedViews = {
    small: ["month", "availability-slot"],
    medium: ["month", "column", "availability-slot"],
    large: ["month", "column", "week", "availability-slot"]
  };

  const viewComponents = {
    month: (
      <MonthView
        key={previouslySelectedTime}
        currentlySelectedSlot={currentlySelectedSlot}
        previouslySelectedTime={previouslySelectedTime}
        handleTimezoneChange={handleTimezoneChange}
        handleFormInput={handleFormInput}
      />
    ),
    column: (
      <ColumnView
        previouslySelectedTime={previouslySelectedTime}
        currentlySelectedSlot={currentlySelectedSlot}
        handleTimezoneChange={handleTimezoneChange}
      />
    ),
    week: (
      <WeekView
        currentlySelectedSlot={currentlySelectedSlot}
        previouslySelectedTime={previouslySelectedTime}
        handleTimezoneChange={handleTimezoneChange}
      />
    ),
    "availability-slot": <AvailabilityView />
  };

  return viewComponents[currentView as keyof typeof viewComponents] || null;
};

const ViewManager = () => {
  const { t } = useTranslation();
  const { ui, actions } = useSchedulerStore();
  const currentlySelectedSlot = useSelectedSlot();
  const { previouslySelectedTime, prevSelectedDuration } = usePreviousSlot();
  // Confirmation screen uses the FROM-slot snapshot so the schedule-success
  // overwrite of prevSelectedSlot doesn't corrupt its strikethrough.
  const {
    previouslySelectedTime: confirmationPrevTime,
    prevSelectedDuration: confirmationPrevDuration
  } = useConfirmationPrevSlot();
  const meetingStatus = useMeetingStatus();
  const { handleFormInput } = useBookingForm();
  const { handleCancel } = useCancellation();
  const { setTimezone } = useMeetingLinkTimezone();

  const handleTimezoneChange = (timezone: string) => {
    setTimezone(timezone);
  };

  const shouldShowSchedulerViews =
    ui?.currentStep === "initial" ||
    ui?.currentStep === "form_inline" ||
    ui?.currentStep === "form" ||
    (ui?.currentStep === "loading" && !meetingStatus);

  return (
    <>
      {shouldShowSchedulerViews && (
        <SchedulerViews
          actions={actions}
          currentView={ui?.currentView || "month"}
          currentlySelectedSlot={currentlySelectedSlot}
          previouslySelectedTime={previouslySelectedTime}
          handleTimezoneChange={handleTimezoneChange}
          handleFormInput={handleFormInput}
        />
      )}
      {meetingStatus && meetingStatus !== SCHEDULE_STATE.AVAILABILITY && (
        <Meeting
          currentlySelectedSlot={currentlySelectedSlot}
          previouslySelectedTime={confirmationPrevTime}
          previouslySelectedDuration={confirmationPrevDuration}
          status={meetingStatus ?? null}
          handleCancel={handleCancel}
          handleReschedule={async (forceInit) => {
            const requestSessionStartedAt = Date.now();
            if (forceInit) {
              await initializeSchedulerStore(
                actions,
                t,
                requestSessionStartedAt
              );
            }
            const latestSessionData = useSchedulerStore.getState().sessionData;
            actions.setSessionData({
              ...(latestSessionData ?? {}),
              isRescheduleFlow: true,
              selectedSlot: null,
              requestSessionStartedAt
            });
            actions.setUiState({
              currentStep: "initial",
              currentView: "month"
            });
          }}
        />
      )}
      {meetingStatus && meetingStatus === SCHEDULE_STATE.AVAILABILITY && (
        <AvailabilityReview
          status={meetingStatus ?? null}
          handleCancel={handleCancel}
          handleReschedule={() => {
            const latestSessionData = useSchedulerStore.getState().sessionData;
            actions.setSessionData({
              ...(latestSessionData ?? {}),
              isRescheduleFlow: true,
              selectedSlot: null,
              requestSessionStartedAt: Date.now()
            });
            actions.setUiState({
              currentStep: "initial",
              currentView: "month"
            });
          }}
        />
      )}
    </>
  );
};

export default ViewManager;
